import { LanguageSelect } from '@components/language_select/language_select.component';
import { tokens } from '@cellula/react-theme-patient';
import { useCurrentLocation } from '../../application/use_current_location';
import styled from 'styled-components';
import { Label, Box, Flex, SafeArea, Stack } from '@cellula/react';
import { useService } from '@visiba/instantiation';
import { UserSettingsService } from '@services/user_settings.service';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import { BreakPoints, useBreakpoint } from '@cellula/react';
import { ASSET_IMAGE_PATH } from '../../constants';
import { AnalyticService } from '@application/analytic/analytic.service';
import { useAnalyticContext } from '@components/analytic_link/analytic_link.provider';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import type { Key } from 'react';

interface Props {
	addressInfoSection: JSX.Element[] | JSX.Element;
	externalLinkSection: JSX.Element | null;
	infoTextSection: JSX.Element | null;
	legalSection: JSX.Element[] | JSX.Element;
	appSection: JSX.Element | null;
	__TEMP__changeLanguage?(langCode: string): void;
}

const elements = {
	/* TODO: Fix Cellula Grid, so it may use min/width/height, asChild and breakpoints */
	container: styled.div`
		display: grid;
		width: 100%;
		min-height: 332px;
		flex-basis: 0%;
		display: grid;
		grid-template-areas:
			'left'
			'middle'
			'right';
		grid-template-columns: 1fr;
		gap: ${tokens.spacing.x3};
		/* TODO: Fix SafeArea padding overriding padding */
		padding-top: ${tokens.spacing.x3} !important;
		/* TODO: Fix SafeArea padding overriding padding */
		padding-bottom: ${tokens.spacing.x4} !important;

		${tokens.breakpointQuery.medium} {
			grid-template-areas: 'left middle right';
			grid-template-columns: 1fr 1fr 1fr;
			gap: var(--cellula--space--x2);
			/* TODO: Fix SafeArea padding overriding padding */
			padding-top: ${tokens.spacing.x6} !important;
			/* TODO: Fix SafeArea padding overriding padding */
			padding-bottom: ${tokens.spacing.x9} !important;
		}
	`,
	languageSelect: styled.div`
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x1};

		${tokens.breakpointQuery.large} {
			max-width: 320px;
		}
	`,
	ceLogo: styled.img`
		max-width: 45px;
	`,
};

interface LanguageSelectSectionProps {
	__TEMP__changeLanguage?(langCode: string): void;
}

function LanguageSelectSection({ __TEMP__changeLanguage }: LanguageSelectSectionProps): JSX.Element | null {
	const [, config] = useCurrentLocation();
	const userSettingsService = useService(UserSettingsService);
	const analyticService = useService(AnalyticService);
	const analyticContext = useAnalyticContext();

	if (config == null) return null;
	if (config.languages.length <= 0) return null;

	function handleLanguageChange(langCode: Key): void {
		const convertedLangCode = I18nAndL10n.convertExternalLocaleToAvailableLocale(langCode as string);
		analyticService.trackEvent(analyticContext, `Changed language to: ${convertedLangCode}`);

		__TEMP__changeLanguage?.(langCode as string);
	}

	return (
		<elements.languageSelect>
			<Label>{withTranslation('PW.Common.ChooseLanguage')}</Label>
			<LanguageSelect
				onChange={handleLanguageChange}
				defaultValue={userSettingsService.getSettings().selectedLanguage}
				languages={
					config.languages.map((language) => ({
						code: language.code,
						key: language.code,
						nativeName: language.nativeName,
					})) ?? []
				}
			/>
		</elements.languageSelect>
	);
}

export function FooterLayout({
	addressInfoSection,
	externalLinkSection,
	infoTextSection,
	legalSection,
	appSection,
	__TEMP__changeLanguage,
}: Props): JSX.Element {
	const breakpoint = useBreakpoint();
	const [, config] = useCurrentLocation();

	return (
		<SafeArea asChild>
			<elements.container>
				<Flex asChild gridArea='left'>
					<Stack gap={tokens.spacing.x2}>
						<LanguageSelectSection __TEMP__changeLanguage={__TEMP__changeLanguage} />

						<Flex as='address'>{addressInfoSection}</Flex>

						{externalLinkSection}
					</Stack>
				</Flex>

				<Flex asChild gridArea='middle'>
					<Stack gap={tokens.spacing.x3}>
						{infoTextSection}

						<Stack gap={tokens.spacing.x1_5}>{legalSection}</Stack>
					</Stack>
				</Flex>
				<Flex
					asChild
					gridArea='right'
					justify='space-between'
					align={breakpoint == BreakPoints.Small ? 'flex-start' : 'flex-end'}
					gap={tokens.spacing.x3}
					wrap='wrap'
				>
					<Box>{appSection}</Box>
				</Flex>
			</elements.container>
		</SafeArea>
	);
}
